import { Box, Button, Flex, IconButton, Text, Tooltip, Portal, VStack, HStack, Badge, useColorModeValue } from '@chakra-ui/react'
import { TimelineCardProps } from './markupTimeline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDrawPolygon, faMapPin, faTrash, faCopy, faStepBackward, faStepForward, faPenToSquare, faGripLinesVertical } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeTimestampFromPixelOffset } from '@/functions/timelineFunctions'

enum DraggingState {
	NotDragging = 0,
	DraggingStart = 1,
	DraggingEnd = 2,
	DraggingAll = 3
}

export const ROW_HEIGHT = 28
export const ROW_GAP = 6

const CustomContextMenu: React.FC<{
	x: number;
	y: number;
	name: string;
	color: string;
	onClose: () => void;
	onEditDetails: () => void;
	onJumpStart: () => void;
	onJumpEnd: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
}> = ({ x, y, name, color, onClose, onEditDetails, onJumpStart, onJumpEnd, onDuplicate, onDelete }) => {
	const containerRef = useRef<HTMLDivElement>(null);

	const bg = useColorModeValue('white', 'gray.850');
	const borderColor = useColorModeValue('gray.200', 'gray.700');
	const hoverBg = useColorModeValue('gray.100', 'gray.750');
	const textColor = useColorModeValue('gray.800', 'white');

	const vWidth = typeof window !== 'undefined' ? window.innerWidth : 1000;
	const vHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

	const isBottomHalf = y > vHeight / 2;
	const isRightHalf = x > vWidth / 2;

	const posStyle: React.CSSProperties = {
		position: 'fixed',
		zIndex: 99999,
		...(isBottomHalf
			? { bottom: `${Math.max(12, vHeight - y)}px`, top: 'auto' }
			: { top: `${Math.max(12, y)}px`, bottom: 'auto' }),
		...(isRightHalf
			? { right: `${Math.max(12, vWidth - x)}px`, left: 'auto' }
			: { left: `${Math.max(12, x)}px`, right: 'auto' })
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('mousedown', handleClickOutside, true);
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('scroll', onClose, true);

		return () => {
			window.removeEventListener('mousedown', handleClickOutside, true);
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('scroll', onClose, true);
		};
	}, [onClose]);

	const menuItems = [
		{ label: 'Edit Properties', icon: faPenToSquare, onClick: onEditDetails },
		{ label: 'Jump to Start', icon: faStepBackward, onClick: onJumpStart },
		{ label: 'Jump to End', icon: faStepForward, onClick: onJumpEnd },
		{ label: 'Duplicate Tag', icon: faCopy, onClick: onDuplicate },
		{
			label: 'Delete Tag',
			icon: faTrash,
			onClick: onDelete,
			color: 'red.400',
			hoverBg: useColorModeValue('red.50', 'red.900'),
			hoverColor: useColorModeValue('red.500', 'red.200')
		}
	];

	return (
		<Portal>
			<Box
				ref={containerRef}
				style={posStyle}
				bg={bg}
				backdropFilter="blur(16px)"
				border="1px solid"
				borderColor={borderColor}
				borderRadius="2xl"
				shadow="2xl"
				py={2}
				minW="200px"
				maxH="calc(100vh - 24px)"
				overflowY="auto"
			>
				<Flex px={3.5} py={1.5} alignItems="center" gap={2} borderBottom="1px solid" borderColor={borderColor} mb={1}>
					<Box w="8px" h="8px" borderRadius="full" bg={color} />
					<Text fontSize="xs" fontWeight="700" noOfLines={1} color={textColor}>
						{name || 'Markup'}
					</Text>
				</Flex>
				<VStack spacing={0.5} align="stretch" px={1}>
					{menuItems.map((item, idx) => (
						<Flex
							key={idx}
							as="button"
							align="center"
							px={3}
							py={2}
							borderRadius="lg"
							fontSize="sm"
							fontWeight="500"
							color={item.color || textColor}
							cursor="pointer"
							transition="all 0.15s"
							_hover={{
								bg: item.hoverBg || hoverBg,
								color: item.hoverColor || item.color || textColor
							}}
							onClick={(e) => {
								e.stopPropagation();
								item.onClick();
								onClose();
							}}
						>
							<Box as="span" mr={3} w="16px" display="flex" justifyContent="center">
								<FontAwesomeIcon icon={item.icon} />
							</Box>
							<Text fontSize="xs" fontWeight="600">
								{item.label}
							</Text>
						</Flex>
					))}
				</VStack>
			</Box>
		</Portal>
	);
};

const TimelineCard: React.FC<TimelineCardProps & {
	videoDuration: number;
	clipStart?: number;
	numColumns: number;
	timelineWidth: number;
	timelineOffset: number;
}> = ({
	color,
	column,
	start,
	end,
	onClick,
	type,
	onDelete,
	onDuplicate,
	onJump,
	onDrag,
	name,
	isSelected,
	videoDuration,
	clipStart = 0,
	timelineWidth,
	timelineOffset
}) => {
	const [localStart, setLocalStart] = useState(start);
	const [localEnd, setLocalEnd] = useState(end);

	const localStartRef = useRef(localStart);
	const localEndRef = useRef(localEnd);

	const dragStartMouseX = useRef(0);
	const dragStartLocalStart = useRef(0);
	const dragStartLocalEnd = useRef(0);

	const [draggingState, setDraggingState] = useState<DraggingState>(DraggingState.NotDragging);
	const [isHovered, setIsHovered] = useState(false);

	const cardLeft = useMemo(() => {
		if (videoDuration <= 0) return 0;
		return Math.round((timelineWidth * (localStart - clipStart)) / videoDuration);
	}, [localStart, clipStart, videoDuration, timelineWidth]);

	const cardWidth = useMemo(() => {
		if (videoDuration <= 0) return 60;
		const rawWidth = Math.round((timelineWidth * (localEnd - localStart)) / videoDuration);
		return Math.max(32, rawWidth); // Ensure minimum touch target
	}, [localStart, localEnd, videoDuration, timelineWidth]);

	const cardTop = useMemo(() => {
		return column * (ROW_HEIGHT + ROW_GAP);
	}, [column]);

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (draggingState === DraggingState.NotDragging) return;
			const minBound = clipStart;
			const maxBound = clipStart + videoDuration;

			if (draggingState === DraggingState.DraggingAll) {
				const deltaX = event.clientX - dragStartMouseX.current;
				const deltaTime = (deltaX * videoDuration) / timelineWidth;

				let newStart = Math.round((dragStartLocalStart.current + deltaTime) * 10) / 10;
				let newEnd = Math.round((dragStartLocalEnd.current + deltaTime) * 10) / 10;

				const duration = dragStartLocalEnd.current - dragStartLocalStart.current;
				if (newStart < minBound) {
					newStart = minBound;
					newEnd = minBound + duration;
				}
				if (newEnd > maxBound) {
					newEnd = maxBound;
					newStart = Math.max(minBound, maxBound - duration);
				}

				setLocalStart(newStart);
				setLocalEnd(newEnd);
				return;
			}

			let timeStamp = Math.round((clipStart + makeTimestampFromPixelOffset(event.clientX - timelineOffset, timelineWidth, videoDuration)) * 10) / 10;
			if (timeStamp < minBound) timeStamp = minBound;
			if (timeStamp > maxBound) timeStamp = maxBound;

			switch (draggingState) {
				case DraggingState.DraggingStart:
					if (timeStamp < localEnd - 0.1) {
						setLocalStart(timeStamp);
					}
					break;
				case DraggingState.DraggingEnd:
					if (timeStamp > localStart + 0.1) {
						setLocalEnd(timeStamp);
					}
					break;
			}
		},
		[timelineWidth, videoDuration, clipStart, draggingState, timelineOffset, localEnd, localStart]
	);

	const handleMouseUp = useCallback(() => {
		onDrag(localStartRef.current, localEndRef.current);
		window.removeEventListener('mousemove', handleMouseMove);
		window.removeEventListener('mouseup', handleMouseUp);
		setDraggingState(DraggingState.NotDragging);
	}, [handleMouseMove, onDrag]);

	useEffect(() => setLocalStart(start), [start]);
	useEffect(() => setLocalEnd(end), [end]);

	useEffect(() => {
		localStartRef.current = localStart;
	}, [localStart]);

	useEffect(() => {
		localEndRef.current = localEnd;
	}, [localEnd]);

	useEffect(() => {
		if (draggingState === DraggingState.NotDragging) {
			window.removeEventListener('mouseup', handleMouseUp);
			return;
		}
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [draggingState, handleMouseMove, handleMouseUp]);

	const [contextMenu, setContextMenu] = useState<{ isOpen: boolean; x: number; y: number }>({ isOpen: false, x: 0, y: 0 });

	const handleContextMenu = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setContextMenu({
			isOpen: true,
			x: e.clientX,
			y: e.clientY
		});
	};

	const duration = useMemo(() => {
		return Math.max(0.1, Math.round((localEnd - localStart) * 10) / 10);
	}, [localStart, localEnd]);

	const badgeTextColor = useColorModeValue('gray.800', 'white');
	const tagBg = useColorModeValue('white', 'gray.800');

	return (
		<Box
			pos="absolute"
			top={`${cardTop}px`}
			left={`${cardLeft}px`}
			width={`${cardWidth}px`}
			height={`${ROW_HEIGHT}px`}
			zIndex={draggingState !== DraggingState.NotDragging ? 150 : isSelected ? 80 : 10}
			onContextMenu={handleContextMenu}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			userSelect="none"
		>
			{contextMenu.isOpen && (
				<CustomContextMenu
					x={contextMenu.x}
					y={contextMenu.y}
					name={name}
					color={color}
					onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
					onEditDetails={onClick}
					onJumpStart={() => onJump(localStart)}
					onJumpEnd={() => onJump(localEnd)}
					onDuplicate={onDuplicate}
					onDelete={onDelete}
				/>
			)}

			<Tooltip
				label={
					<VStack spacing={0.5} align="start" py={0.5}>
						<Text fontWeight="bold">{name || (type === 'polygon' ? 'Polygon' : 'Hotspot')}</Text>
						<Text fontSize="xs" opacity={0.85}>
							Time: {localStart.toFixed(1)}s – {localEnd.toFixed(1)}s ({duration.toFixed(1)}s)
						</Text>
						<Text fontSize="2xs" color="gray.400">
							Right-click for options • Drag edges to resize
						</Text>
					</VStack>
				}
				placement="top"
				hasArrow
				bg="gray.900"
				color="white"
				borderRadius="xl"
				py={1.5}
				px={3}
				openDelay={400}
				isDisabled={draggingState !== DraggingState.NotDragging}
			>
				<Flex
					pos="relative"
					w="100%"
					h="100%"
					borderRadius="xl"
					bg={tagBg}
					border="1.5px solid"
					borderColor={isSelected ? color : `${color}88`}
					boxShadow={
						isSelected
							? `0 0 0 2px ${color}, 0 4px 14px ${color}55`
							: isHovered
							? `0 4px 12px ${color}44`
							: '0 2px 6px rgba(0,0,0,0.06)'
					}
					overflow="hidden"
					cursor={draggingState === DraggingState.DraggingAll ? 'grabbing' : 'grab'}
					transition="box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease"
					transform={isHovered && draggingState === DraggingState.NotDragging ? 'translateY(-1px)' : 'none'}
					onMouseDown={(e) => {
						if (e.button !== 0) return; // Only left click
						e.stopPropagation();
						onClick(); // Select on mouse down
						dragStartMouseX.current = e.clientX;
						dragStartLocalStart.current = localStart;
						dragStartLocalEnd.current = localEnd;
						setDraggingState(DraggingState.DraggingAll);
					}}
				>
					{/* Tinted background overlay matching color */}
					<Box
						pos="absolute"
						top={0}
						left={0}
						right={0}
						bottom={0}
						bg={`${color}24`}
						pointerEvents="none"
					/>

					{/* Left Resize Handle */}
					<Box
						zIndex={20}
						pos="absolute"
						left={0}
						top={0}
						bottom={0}
						w="10px"
						bg={color}
						cursor="ew-resize"
						display="flex"
						alignItems="center"
						justifyContent="center"
						opacity={0.85}
						_hover={{ opacity: 1, w: '12px' }}
						transition="width 0.15s, opacity 0.15s"
						onMouseDown={(e) => {
							e.stopPropagation();
							setDraggingState(DraggingState.DraggingStart);
						}}
					>
						<Box w="1.5px" h="12px" bg="white" borderRadius="full" opacity={0.9} />
					</Box>

					{/* Right Resize Handle */}
					<Box
						zIndex={20}
						pos="absolute"
						right={0}
						top={0}
						bottom={0}
						w="10px"
						bg={color}
						cursor="ew-resize"
						display="flex"
						alignItems="center"
						justifyContent="center"
						opacity={0.85}
						_hover={{ opacity: 1, w: '12px' }}
						transition="width 0.15s, opacity 0.15s"
						onMouseDown={(e) => {
							e.stopPropagation();
							setDraggingState(DraggingState.DraggingEnd);
						}}
					>
						<Box w="1.5px" h="12px" bg="white" borderRadius="full" opacity={0.9} />
					</Box>

					{/* Card Content Area */}
					<Flex
						alignItems="center"
						justifyContent="space-between"
						w="100%"
						h="100%"
						pl="14px"
						pr="14px"
						gap={1.5}
						overflow="hidden"
					>
						<HStack spacing={1.5} minW={0} overflow="hidden">
							<Box
								w="20px"
								h="20px"
								borderRadius="md"
								bg={`${color}33`}
								display="flex"
								alignItems="center"
								justifyContent="center"
								flexShrink={0}
							>
								<FontAwesomeIcon
									icon={type === 'polygon' ? faDrawPolygon : faMapPin}
									style={{ fontSize: '11px', color }}
								/>
							</Box>
							<Text
								noOfLines={1}
								fontSize="xs"
								fontWeight="700"
								color={badgeTextColor}
								letterSpacing="tight"
							>
								{name || (type === 'polygon' ? 'Polygon' : 'Hotspot')}
							</Text>
						</HStack>

						{/* Duration badge if width is sufficient */}
						{cardWidth > 110 && (
							<Badge
								size="sm"
								fontSize="2xs"
								fontWeight="700"
								borderRadius="md"
								px={1.5}
								py={0.2}
								bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.200')}
								color={useColorModeValue('gray.700', 'gray.200')}
								flexShrink={0}
							>
								{duration.toFixed(1)}s
							</Badge>
						)}
					</Flex>
				</Flex>
			</Tooltip>
		</Box>
	);
};

export default TimelineCard;
